import { service } from '@ember/service';
import Component from '@glimmer/component';
import type NotificationService from 'bcl/services/notification';
import removeNotificationAfterTime from 'bcl/modifiers/remove-notification-after-time';
import eq from 'ember-truth-helpers/helpers/eq';
import HeroIcon from 'bcl/components/hero-icon';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';

interface NotificationAreaSignature {
  // The arguments accepted by the component
  Args: {};
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class NotificationAreaComponent extends Component<NotificationAreaSignature> {
  <template>
    {{! Global notification live region, render this permanently at the end of the document }}
    <div
      aria-live="assertive"
      class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-100"
    >
      <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
        {{!
      Notification panel, dynamically insert this into the live region when it needs to be displayed

      Entering: "transform ease-out duration-300 transition"
        From: "translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        To: "translate-y-0 opacity-100 sm:translate-x-0"
      Leaving: "transition ease-in duration-100"
        From: "opacity-100"
        To: "opacity-0"
    }}
        {{#each this.notification.notifications as |notification|}}
          <div
            {{removeNotificationAfterTime notification}}
            class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white ring-1 shadow-lg ring-black/5"
          >
            <div class="p-4">
              <div class="flex items-start">
                <div class="shrink-0">
                  {{#if (eq notification.type "error")}}
                    <HeroIcon @icon="x-circle" class="text-red-400 size-6" />
                  {{else if (eq notification.type "warning")}}
                    <HeroIcon
                      @icon="exclamation-circle"
                      class="text-yellow-400 size-6"
                    />
                  {{else if (eq notification.type "success")}}
                    <HeroIcon
                      @icon="check-circle"
                      class="text-green-400 size-6"
                    />
                  {{else}}
                    <HeroIcon
                      @icon="information-circle"
                      class="text-blue-400 size-6"
                    />
                  {{/if}}

                </div>
                <div class="ml-3 w-0 flex-1 pt-0.5">
                  <p
                    class="text-sm font-medium text-gray-900"
                  >{{notification.header}}</p>
                  <p
                    class="mt-1 text-sm text-gray-500"
                  >{{notification.message}}</p>
                </div>
                <div class="ml-4 flex shrink-0">
                  <button
                    {{on
                      "click"
                      (fn this.notification.removeNotification notification)
                    }}
                    type="button"
                    class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                  >
                    <span class="sr-only">Schließen</span>
                    <HeroIcon @icon="x-mark" class="size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

        {{/each}}
      </div>
    </div>
  </template>
  @service declare notification: NotificationService;
}
